/**
 * Android app (TWA) only: "Enjoying Spliiit? Rate us on Google Play".
 * The Android app is a web wrapper, so Google's native in-app review sheet
 * isn't available; this card links to the Play listing instead. No stars and
 * no "only happy people go to the store" routing — both stores ban that.
 *
 * Shows on the 1st, 4th, 7th… app open (after sign-in and first-run) until
 * they tap "Rate on Google Play". "Not now" just closes it for this open.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { isInTWA } from "@/lib/platform";
import { track } from "@/lib/analytics";

const PLAY_URL = "https://play.google.com/store/apps/details?id=ca.klarityit.spliiit";
const K_OPENS = "spliiit_play_rate_opens";
const K_DONE = "spliiit_play_rate_done";
const EVERY = 3;
const DELAY_MS = 2500;
const BUSY_RETRY_MS = 3000;

let countedThisOpen = false;

function get(k: string) { try { return localStorage.getItem(k); } catch { return null; } }
function set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* storage off */ } }

export function PlayRatingCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ready = isInTWA && !!user && !!user.defaultCurrency && !!user.firstRunCompletedAt;

  useEffect(() => {
    if (!ready || countedThisOpen || get(K_DONE)) return;
    countedThisOpen = true;
    const opens = (Number(get(K_OPENS)) || 0) + 1;
    set(K_OPENS, String(opens));
    if ((opens - 1) % EVERY !== 0) return;
    // Never on top of another sheet or popup (What's New, add expense…): wait
    // for it to close, checking every few seconds.
    let t = 0;
    const tryShow = () => {
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) { t = window.setTimeout(tryShow, BUSY_RETRY_MS); return; }
      setOpen(true);
      track("play_rate_card_shown", { opens });
    };
    t = window.setTimeout(tryShow, DELAY_MS);
    return () => window.clearTimeout(t);
  }, [ready]);

  if (!open) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+88px)] pointer-events-none" data-testid="play-rate-card">
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-card border border-border shadow-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl flex-none" />
          <div className="min-w-0">
            <p className="font-semibold text-foreground">Enjoying Spliiit?</p>
            <p className="text-sm text-muted-foreground leading-snug">A quick rating on Google Play helps your friends find us.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="h-11 rounded-full border border-border text-sm font-medium" onClick={() => { setOpen(false); track("play_rate_card_later"); }} data-testid="play-rate-later">
            Not now
          </button>
          <button
            type="button"
            className="h-11 rounded-full bg-foreground text-background text-sm font-medium"
            onClick={() => { set(K_DONE, String(Date.now())); setOpen(false); track("play_rate_card_rate"); window.open(PLAY_URL, "_blank"); }}
            data-testid="play-rate-go"
          >
            Rate on Google Play
          </button>
        </div>
      </div>
    </div>
  );
}
